import { useMemo, useState } from 'react'
import { StatInterval, StatSeries } from '@/models/stats.ts'
import { colorForSeries } from './statColors.ts'

interface StatChartProps {
  buckets: string[]
  series: StatSeries[]
  interval?: StatInterval
  /** Plot height in px (without axis labels). */
  height?: number
}

/** Bucket → axis label. Day/week come back as 'YYYY-MM-DD', hour as ISO. */
export function formatBucket(bucket: string, interval: StatInterval): string {
  if (interval === 'hour') {
    const [date, time] = bucket.split('T')
    return `${date.slice(8, 10)}.${date.slice(5, 7)}. ${(time ?? '').slice(0, 2)} Uhr`
  }
  if (interval === 'month') {
    return `${bucket.slice(5, 7)}/${bucket.slice(2, 4)}`
  }
  return `${bucket.slice(8, 10)}.${bucket.slice(5, 7)}.`
}

/** Rounds the axis maximum up to something readable (1, 2, 5 × 10ⁿ). */
function niceMax(value: number): number {
  if (value <= 5) return Math.max(1, value)
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const normalized = value / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return step * magnitude
}

/**
 * Stacked bar chart, no chart library: the values arrive pre-aggregated and
 * index-parallel to `buckets`, so there is nothing left to compute.
 */
export default function StatChart({ buckets, series, interval = 'day', height = 200 }: StatChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const { totals, max, labelStep } = useMemo(() => {
    const totals = buckets.map((_, index) => series.reduce((sum, s) => sum + (s.values[index] ?? 0), 0))
    return {
      totals,
      max: niceMax(Math.max(1, ...totals)),
      // At most ~8 labels, however wide the range is.
      labelStep: Math.max(1, Math.ceil(buckets.length / 8)),
    }
  }, [buckets, series])

  if (buckets.length === 0) {
    return <p className='text-sm opacity-50 py-8 text-center'>Keine Daten im gewählten Zeitraum</p>
  }

  // Bottom series first: in a bottom-aligned flex column that is the last child.
  const stacked = [...series].map((s, index) => ({ series: s, color: colorForSeries(s, index) })).reverse()

  return (
    <div className='relative'>
      <div className='flex'>
        {/* Y axis */}
        <div
          className='flex flex-col justify-between items-end pr-2 text-[11px] opacity-50 tabular-nums'
          style={{ height }}
        >
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>

        <div className='flex-1 min-w-0'>
          {/* Plot area with recessive grid lines */}
          <div className='relative' style={{ height }}>
            <div className='absolute inset-0 flex flex-col justify-between pointer-events-none'>
              <div className='border-t border-[var(--portal-border)]' />
              <div className='border-t border-[var(--portal-border)]' />
              <div className='border-t border-[var(--portal-border)]' />
            </div>

            <div className='absolute inset-0 flex items-end gap-[3px]'>
              {buckets.map((bucket, index) => (
                <div
                  key={bucket}
                  className='flex-1 min-w-0 h-full flex flex-col justify-end gap-[2px] rounded transition-colors'
                  style={{ background: hoveredIndex === index ? 'var(--portal-sidebar-bg)' : 'transparent' }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {totals[index] === 0 ? (
                    <div className='h-[2px] bg-[var(--portal-border)] rounded-full' />
                  ) : (
                    stacked.map((entry, stackIndex) => {
                      const value = entry.series.values[index] ?? 0
                      if (!value) return null
                      // Topmost visible segment: everything above it is zero.
                      const isTop = stacked.slice(0, stackIndex).every(other => !(other.series.values[index] ?? 0))
                      return (
                        <div
                          key={entry.series.key}
                          title={`${entry.series.label}: ${value}`}
                          style={{
                            height: `${(value / max) * 100}%`,
                            minHeight: 2,
                            background: entry.color,
                            borderRadius: isTop ? '4px 4px 0 0' : 0,
                          }}
                        />
                      )
                    })
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* X axis */}
          <div className='flex gap-[3px] mt-2'>
            {buckets.map((bucket, index) => (
              <div
                key={bucket}
                className='flex-1 min-w-0 text-center text-[11px] opacity-50 whitespace-nowrap overflow-hidden'
              >
                {index % labelStep === 0 ? formatBucket(bucket, interval) : ''}
              </div>
            ))}
          </div>
        </div>
      </div>

      {hoveredIndex !== null && (
        <div
          className='absolute top-0 z-20 pointer-events-none rounded-lg bg-[black] bg-opacity-90 text-[white] px-3 py-2 text-xs shadow-lg whitespace-nowrap'
          style={{
            // Clamped so the tooltip never leaves the card at the edges.
            left: `${Math.min(92, Math.max(8, ((hoveredIndex + 0.5) / buckets.length) * 100))}%`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className='opacity-70 mb-1'>{formatBucket(buckets[hoveredIndex], interval)}</div>
          {series.map((s, index) => (
            <div key={s.key} className='flex items-center gap-2'>
              <span
                className='w-2 h-2 rounded-sm flex-shrink-0'
                style={{ background: colorForSeries(s, index) }}
              />
              <span className='opacity-80'>{s.label}:</span>
              <span className='font-semibold tabular-nums'>{s.values[hoveredIndex] ?? 0}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** Identity is never colour alone — the legend is always rendered for ≥ 2 series. */
export function StatLegend({ series }: { series: StatSeries[] }) {
  if (series.length < 2) return null

  return (
    <div className='flex flex-wrap gap-x-5 gap-y-2 text-sm'>
      {series.map((s, index) => (
        <span key={s.key} className='flex items-center gap-2'>
          <span className='w-2.5 h-2.5 rounded-sm flex-shrink-0' style={{ background: colorForSeries(s, index) }} />
          <span className='opacity-70'>{s.label}</span>
          <span className='font-medium tabular-nums'>{s.total}</span>
        </span>
      ))}
    </div>
  )
}
